import { z } from 'zod'

export const PersonaSchema = z.object({
  id: z.string(),
  workspace_id: z.string(),
  user_id: z.string(),
  name: z.string(),
  role: z.string(),
  description: z.string().nullable(),
  system_prompt: z.string(),
  default_provider: z.string().nullable(),
  default_model: z.string().nullable(),
  tools: z.array(z.any()),
  color: z.string().nullable(),
  icon_slug: z.string().nullable(),
  temperature: z.number(),
  max_iterations: z.number(),
  is_public: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
})

export type Persona = z.infer<typeof PersonaSchema>

export interface PersonaCreateRequest {
  name: string
  role: string
  description?: string | null
  system_prompt?: string
  default_provider?: string | null
  default_model?: string | null
  tools?: unknown[]
  color?: string | null
  icon_slug?: string | null
  temperature?: number
  max_iterations?: number
  is_public?: boolean
}

export type PersonaUpdateRequest = Partial<PersonaCreateRequest>

export const PERSONA_PRESETS: PersonaCreateRequest[] = [
  {
    name: 'Researcher',
    role: 'researcher',
    description: 'Gathers information, verifies sources, produces briefs.',
    system_prompt:
      'You are a rigorous research analyst. Investigate the user request using your tools, verify claims across at least two sources when possible, and return a concise brief with citations.',
    icon_slug: 'Search',
    color: '#0ea5e9',
    temperature: 0.2,
  },
  {
    name: 'Planner',
    role: 'planner',
    description: 'Decomposes goals into a task DAG.',
    system_prompt:
      'You break large goals into small, well-scoped tasks. Each task has a clear owner, expected output, and dependencies. Prefer parallel tasks where possible.',
    icon_slug: 'ListChecks',
    color: '#8b5cf6',
    temperature: 0.2,
  },
  {
    name: 'Writer',
    role: 'writer',
    description: 'Turns research + outlines into finished copy.',
    system_prompt:
      'You write clear, honest, engaging copy. Use short sentences. Prefer concrete examples. Match the requested tone.',
    icon_slug: 'PenLine',
    color: '#f59e0b',
    temperature: 0.6,
  },
  {
    name: 'Reviewer',
    role: 'reviewer',
    description: 'Critiques work against a rubric before it ships.',
    system_prompt:
      'You are a strict but fair reviewer. Score the submitted work on correctness, completeness, and clarity. Point to specific problems and suggest the smallest edit that fixes each.',
    icon_slug: 'Shield',
    color: '#f43f5e',
    temperature: 0.1,
  },
  {
    name: 'Coder',
    role: 'coder',
    description: 'Writes and refactors code with test coverage.',
    system_prompt:
      'You write small, correct diffs. Prefer editing existing files. Never invent APIs. Run tools to verify before claiming done.',
    icon_slug: 'Code',
    color: '#10b981',
    temperature: 0.1,
  },
  {
    name: 'Critic',
    role: 'critic',
    description: 'Adversarially probes decisions before commit.',
    system_prompt:
      'You look for what will break, get misused, or bite in production. State the failure mode, the likelihood, and the cheapest safeguard.',
    icon_slug: 'AlertTriangle',
    color: '#ec4899',
    temperature: 0.3,
  },
]
